import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Mail, Inbox, Send, Search, RefreshCw, ArrowLeft,
  Reply, ReplyAll, X, ChevronDown, Loader2, Link2Off, Link2,
  Star, Archive, Trash2, Forward, Paperclip, FileText,
  Download, Eye, EyeOff, Users, Tag, Bell, MessageCircle,
  MoreHorizontal, CheckCheck
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

function parseEmail(raw) {
  if (!raw) return { name: '', email: '' };
  const m = raw.match(/^(.+?)\s*<(.+?)>$/);
  if (m) return { name: m[1].trim().replace(/"/g, ''), email: m[2] };
  return { name: '', email: raw.trim() };
}

function decodeHtml(html) {
  if (!html) return '';
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
}

function fmtDate(dateStr, ts) {
  try {
    const d = ts ? new Date(parseInt(ts)) : new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 86400000) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diff < 604800000) return d.toLocaleDateString([], { weekday: 'short' });
    if (d.getFullYear() === now.getFullYear()) return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return dateStr; }
}

function fmtFullDate(dateStr, ts) {
  try {
    const d = ts ? new Date(parseInt(ts)) : new Date(dateStr);
    return d.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return dateStr; }
}

function fmtSize(b) {
  if (!b) return '';
  if (b < 1024) return b + ' B';
  if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
  return (b / 1048576).toFixed(1) + ' MB';
}

// ─── Category Tabs (Gmail-style) ───
const CATEGORIES = [
  { key: 'primary', label: 'Primary', icon: Inbox, query: 'category:primary' },
  { key: 'social', label: 'Social', icon: Users, query: 'category:social' },
  { key: 'promotions', label: 'Promotions', icon: Tag, query: 'category:promotions' },
  { key: 'updates', label: 'Updates', icon: Bell, query: 'category:updates' },
  { key: 'forums', label: 'Forums', icon: MessageCircle, query: 'category:forums' },
];

// ─── Connect Prompt ───
function ConnectPrompt({ onConnect, loading }) {
  return (
    <div className="flex-1 flex items-center justify-center" data-testid="gmail-connect-prompt">
      <div className="text-center space-y-4 max-w-md">
        <div className="w-16 h-16 rounded-full bg-sky-50 flex items-center justify-center mx-auto">
          <Mail className="w-8 h-8 text-sky-600" />
        </div>
        <h2 className="text-xl font-semibold text-slate-900">Connect Your Gmail</h2>
        <p className="text-sm text-slate-500">
          Link your mailbox to read, reply, and send emails directly from the CRM.
        </p>
        <Button onClick={onConnect} disabled={loading} className="bg-[#00709B] hover:bg-[#005a7d]" data-testid="gmail-connect-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
          Connect Gmail
        </Button>
      </div>
    </div>
  );
}

// ─── Compose Modal ───
function ComposeModal({ onClose, onSend, replyTo, replyAll, forwardMsg, threadId, userEmail }) {
  const isForward = !!forwardMsg;
  const isReply = !!replyTo;

  // For Reply All: To = original sender, CC = all other recipients (excluding self)
  const buildReplyAllRecipients = () => {
    if (!replyTo || !replyAll) return { to: '', cc: '' };
    const sender = parseEmail(replyTo.from).email;
    const allTo = (replyTo.to || '').split(',').map(e => parseEmail(e.trim()).email).filter(Boolean);
    const allCc = (replyTo.cc || '').split(',').map(e => parseEmail(e.trim()).email).filter(Boolean);
    const self = (userEmail || '').toLowerCase();
    const ccList = [...allTo, ...allCc].filter(e => e.toLowerCase() !== self && e.toLowerCase() !== sender.toLowerCase());
    return { to: sender, cc: [...new Set(ccList)].join(', ') };
  };

  const replyAllRecipients = buildReplyAllRecipients();

  const [to, setTo] = useState(
    isReply ? (replyAll ? replyAllRecipients.to : parseEmail(replyTo.from).email) : ''
  );
  const [cc, setCc] = useState(isReply && replyAll ? replyAllRecipients.cc : '');
  const [subject, setSubject] = useState(
    isForward ? `Fwd: ${forwardMsg.subject?.replace(/^Fwd:\s*/i, '')}` :
    isReply ? `Re: ${replyTo.subject?.replace(/^Re:\s*/i, '')}` : ''
  );
  const [body, setBody] = useState(
    isForward ? `\n\n---------- Forwarded message ----------\nFrom: ${forwardMsg.from}\nDate: ${forwardMsg.date}\nSubject: ${forwardMsg.subject}\nTo: ${forwardMsg.to}\n\n${forwardMsg.body_text || forwardMsg.snippet || ''}` : ''
  );
  const [sending, setSending] = useState(false);
  const [showCc, setShowCc] = useState(!!cc);
  const [toSuggestions, setToSuggestions] = useState([]);
  const [showToSuggestions, setShowToSuggestions] = useState(false);
  const toTimeout = useRef(null);

  const searchContacts = async (val) => {
    if (val.length < 2) { setToSuggestions([]); return; }
    // Extract last token (after comma) for autocomplete
    const lastToken = val.split(',').pop().trim();
    if (lastToken.length < 2) { setToSuggestions([]); return; }
    try {
      const r = await axios.get(`${API}/gmail/contact-suggestions`, { params: { q: lastToken }, withCredentials: true });
      setToSuggestions(r.data || []);
      setShowToSuggestions(r.data?.length > 0);
    } catch { setToSuggestions([]); }
  };

  const handleToChange = (val) => {
    setTo(val);
    if (toTimeout.current) clearTimeout(toTimeout.current);
    toTimeout.current = setTimeout(() => searchContacts(val), 300);
  };

  const selectSuggestion = (s) => {
    const parts = to.split(',').map(p => p.trim()).filter(Boolean);
    parts.pop();
    parts.push(s.email);
    setTo(parts.join(', ') + (parts.length > 0 ? '' : ''));
    setShowToSuggestions(false);
    setToSuggestions([]);
  };

  const handleSend = async () => {
    if (!to) return toast.error('Recipient is required');
    setSending(true);
    try {
      await onSend({
        to, cc, subject,
        body: body.replace(/\n/g, '<br>'),
        reply_to_message_id: isReply ? replyTo.id : undefined,
        thread_id: isReply ? threadId : undefined,
      });
      toast.success('Email sent!');
      onClose();
    } catch (e) {
      toast.error('Failed to send: ' + (e.response?.data?.detail || e.message));
    }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-end justify-center sm:items-center p-4" data-testid="compose-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col border">
        <div className="flex items-center justify-between px-4 py-2.5 border-b bg-slate-800 rounded-t-xl">
          <h3 className="text-sm font-medium text-white">
            {isForward ? 'Forward' : (isReply && replyAll) ? 'Reply All' : isReply ? 'Reply' : 'New Message'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-3 space-y-1.5 flex-1 overflow-y-auto">
          <div className="flex items-center border-b pb-1.5 relative">
            <span className="text-xs text-slate-400 w-10 shrink-0">To</span>
            <input value={to} onChange={e => handleToChange(e.target.value)}
              onFocus={() => { if (toSuggestions.length) setShowToSuggestions(true); }}
              onBlur={() => setTimeout(() => setShowToSuggestions(false), 200)}
              placeholder="Recipients" className="flex-1 text-sm outline-none text-slate-800 placeholder:text-slate-300" data-testid="compose-to" />
            {!showCc && (
              <button onClick={() => setShowCc(true)} className="text-xs text-slate-400 hover:text-slate-600 shrink-0">Cc</button>
            )}
            {showToSuggestions && toSuggestions.length > 0 && (
              <div className="absolute top-full left-10 right-0 mt-1 bg-white rounded-lg shadow-lg border z-50 max-h-48 overflow-y-auto" data-testid="to-suggestions">
                {toSuggestions.map((s, i) => (
                  <button key={i} onMouseDown={() => selectSuggestion(s)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 text-xs">
                    <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center text-sky-700 text-[10px] font-bold shrink-0">
                      {(s.name || s.email)[0].toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="font-medium text-slate-700 block truncate">{s.name || s.email}</span>
                      <span className="text-slate-400 block truncate">{s.email}{s.company ? ` · ${s.company}` : ''}</span>
                    </div>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full shrink-0 ${s.type === 'pharma' ? 'bg-teal-50 text-teal-600' : 'bg-amber-50 text-amber-600'}`}>
                      {s.type}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {showCc && (
            <div className="flex items-center border-b pb-1.5">
              <span className="text-xs text-slate-400 w-10 shrink-0">Cc</span>
              <input value={cc} onChange={e => setCc(e.target.value)} placeholder="Cc" className="flex-1 text-sm outline-none text-slate-800 placeholder:text-slate-300" data-testid="compose-cc" />
            </div>
          )}
          <div className="flex items-center border-b pb-1.5">
            <span className="text-xs text-slate-400 w-10 shrink-0">Subj</span>
            <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject" className="flex-1 text-sm outline-none text-slate-800 placeholder:text-slate-300" data-testid="compose-subject" />
          </div>
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Compose email"
            className="w-full min-h-[180px] text-sm text-slate-800 resize-none focus:outline-none pt-2 placeholder:text-slate-300"
            data-testid="compose-body"
            autoFocus
          />
          {/* Signature preview */}
          <div className="border-t border-slate-100 pt-2 mt-1" data-testid="compose-signature-preview">
            <p className="text-xs text-slate-400 leading-relaxed">
              —<br/>
              <span className="font-semibold text-slate-500">Dr. Amit Dang</span> (he/him) | Founder &amp; CEO<br/>
              <a href="https://marksmanhealthcare.com/" target="_blank" rel="noopener noreferrer" className="text-[#00709B] hover:underline">MarksMan Healthcare</a> - HEOR | Medical Affairs | RWE<br/>
              <a href="https://foresightrx.com/" target="_blank" rel="noopener noreferrer" className="text-[#00709B] hover:underline">ForesightRx</a> | Out-Licensing | Fund Raising<br/>
              +91 7738389300 (direct) | +91 91604 67576 (office)
            </p>
          </div>
        </div>
        <div className="flex items-center justify-between px-4 py-2.5 border-t bg-slate-50 rounded-b-xl">
          <Button onClick={handleSend} disabled={sending} size="sm" className="bg-[#1a73e8] hover:bg-[#1557b0] rounded-full px-6" data-testid="compose-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Send
          </Button>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}

// ─── Attachment Bar ───
function AttachmentBar({ messageId, attachments }) {
  const [dl, setDl] = useState(null);
  const handleDownload = async (att) => {
    setDl(att.id);
    try {
      const r = await axios.get(`${API}/gmail/messages/${messageId}/attachments/${att.id}`, { withCredentials: true });
      const byteChars = atob(r.data.data);
      const byteArr = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
      const blob = new Blob([byteArr], { type: att.mimeType || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = att.filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download'); }
    setDl(null);
  };
  if (!attachments?.length) return null;
  return (
    <div className="flex flex-wrap gap-2 px-4 py-2 border-t bg-slate-50/50">
      {attachments.map(att => (
        <button key={att.id} onClick={() => handleDownload(att)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded border bg-white hover:bg-slate-50 text-xs text-slate-600 transition-colors"
          data-testid={`attachment-${att.filename}`}>
          {dl === att.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3 text-slate-400" />}
          <span className="truncate max-w-[140px]">{att.filename}</span>
          {att.size > 0 && <span className="text-slate-400">({fmtSize(att.size)})</span>}
          <Download className="w-3 h-3 text-slate-400" />
        </button>
      ))}
    </div>
  );
}

// ─── Message Detail ───
function MessageDetail({ message, onBack, onReply, onReplyAll, onForward, onStar, onArchive, onTrash, onMarkUnread, threadMessages }) {
  const msgs = threadMessages || [message];
  const [attachments, setAttachments] = useState({});
  const [expandedMsgs, setExpandedMsgs] = useState(new Set([msgs[msgs.length - 1]?.id]));

  useEffect(() => {
    msgs.forEach(async (m) => {
      if (m.has_attachments && !attachments[m.id]) {
        try {
          const r = await axios.get(`${API}/gmail/messages/${m.id}/attachments`, { withCredentials: true });
          setAttachments(prev => ({ ...prev, [m.id]: r.data.attachments }));
        } catch { /* ignore */ }
      }
    });
  }, [msgs]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid="message-detail">
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-3 py-2 border-b bg-white shrink-0">
        <button onClick={onBack} className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <button onClick={() => onArchive(message)} className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500" title="Archive" data-testid="archive-btn">
          <Archive className="w-4 h-4" />
        </button>
        <button onClick={() => onTrash(message)} className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500 hover:text-red-500" title="Delete" data-testid="trash-btn">
          <Trash2 className="w-4 h-4" />
        </button>
        <button onClick={() => onMarkUnread(message)} className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500" title="Mark unread" data-testid="mark-unread-btn">
          <EyeOff className="w-4 h-4" />
        </button>
        <div className="flex-1" />
        {/* Reply actions — top right like Gmail */}
        <button onClick={() => onReply(msgs[msgs.length - 1])} className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500" title="Reply" data-testid="reply-btn">
          <Reply className="w-4 h-4" />
        </button>
        <button onClick={() => onReplyAll(msgs[msgs.length - 1])} className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500" title="Reply All" data-testid="reply-all-btn">
          <ReplyAll className="w-4 h-4" />
        </button>
        <button onClick={() => onForward(msgs[msgs.length - 1])} className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500" title="Forward" data-testid="forward-btn">
          <Forward className="w-4 h-4" />
        </button>
      </div>

      {/* Subject + star */}
      <div className="flex items-start gap-2 px-5 pt-4 pb-2">
        <h2 className="text-lg font-normal text-slate-900 flex-1">{message.subject}</h2>
        <button onClick={() => onStar(message)} className={`p-1 ${message.is_starred ? 'text-amber-400' : 'text-slate-300 hover:text-amber-400'}`} data-testid="star-btn">
          <Star className="w-5 h-5" fill={message.is_starred ? 'currentColor' : 'none'} />
        </button>
      </div>

      {/* Thread */}
      <div className="flex-1 overflow-y-auto px-5 pb-4 space-y-3">
        {msgs.map((m, i) => {
          const sender = parseEmail(m.from);
          const isExpanded = expandedMsgs.has(m.id) || i === msgs.length - 1;
          return (
            <div key={m.id || i} className="group">
              {/* Sender header */}
              <button
                onClick={() => { const s = new Set(expandedMsgs); if (s.has(m.id)) s.delete(m.id); else s.add(m.id); setExpandedMsgs(s); }}
                className="w-full flex items-start gap-3 text-left py-2"
              >
                <div className="w-9 h-9 rounded-full bg-sky-600 flex items-center justify-center shrink-0 text-white text-sm font-medium">
                  {(sender.name || sender.email || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-medium text-slate-900">{sender.name || sender.email}</span>
                    <span className="text-[11px] text-slate-400">&lt;{sender.email}&gt;</span>
                  </div>
                  {isExpanded ? (
                    <p className="text-[11px] text-slate-400 mt-0.5">to {m.to}{m.cc ? `, cc: ${m.cc}` : ''}</p>
                  ) : (
                    <p className="text-xs text-slate-400 truncate">{decodeHtml(m.snippet)}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[11px] text-slate-400">{fmtFullDate(m.date, m.internalDate)}</span>
                  {m.has_attachments && <Paperclip className="w-3 h-3 text-slate-400" />}
                </div>
              </button>

              {/* Body */}
              {isExpanded && (
                <div className="pl-12">
                  <div className="py-3">
                    {m.body_html ? (
                      <div className="prose prose-sm max-w-none text-slate-700 [&_img]:max-w-full [&_a]:text-sky-600 [&_blockquote]:border-l-2 [&_blockquote]:border-slate-300 [&_blockquote]:pl-3 [&_blockquote]:text-slate-500" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(m.body_html || '', { ADD_ATTR: ['target'] }) }} />
                    ) : (
                      <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">{m.body_text || decodeHtml(m.snippet)}</pre>
                    )}
                  </div>
                  <AttachmentBar messageId={m.id} attachments={attachments[m.id]} />
                </div>
              )}
            </div>
          );
        })}

        {/* Quick reply bar at bottom */}
        <div className="flex items-center gap-2 pt-3 pl-12 border-t mt-3">
          <button onClick={() => onReply(msgs[msgs.length - 1])} className="flex items-center gap-1.5 px-4 py-2 rounded-full border text-xs text-slate-500 hover:bg-slate-50 transition-colors" data-testid="reply-bottom-btn">
            <Reply className="w-3.5 h-3.5" /> Reply
          </button>
          <button onClick={() => onReplyAll(msgs[msgs.length - 1])} className="flex items-center gap-1.5 px-4 py-2 rounded-full border text-xs text-slate-500 hover:bg-slate-50 transition-colors" data-testid="reply-all-bottom-btn">
            <ReplyAll className="w-3.5 h-3.5" /> Reply All
          </button>
          <button onClick={() => onForward(msgs[msgs.length - 1])} className="flex items-center gap-1.5 px-4 py-2 rounded-full border text-xs text-slate-500 hover:bg-slate-50 transition-colors" data-testid="forward-bottom-btn">
            <Forward className="w-3.5 h-3.5" /> Forward
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ───
export default function GmailPage() {
  const [connected, setConnected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [folder, setFolder] = useState('INBOX');
  const [category, setCategory] = useState('primary');
  const [searchQ, setSearchQ] = useState('');
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [threadMessages, setThreadMessages] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [replyAll, setReplyAll] = useState(false);
  const [forwardMsg, setForwardMsg] = useState(null);
  const [replyThreadId, setReplyThreadId] = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [unreadData, setUnreadData] = useState({ count: 0, categories: {} });
  const [showOperators, setShowOperators] = useState(false);
  const [activeChips, setActiveChips] = useState(new Set());
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const searchTimeout = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/oauth/gmail/status`, { withCredentials: true })
      .then(r => setConnected(r.data.connected))
      .catch(() => setConnected(false));
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/gmail/unread-count`, { withCredentials: true });
      setUnreadData(r.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (connected) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 60000);
      return () => clearInterval(interval);
    }
  }, [connected, fetchUnreadCount]);

  const fetchMessages = useCallback(async (pageToken = null) => {
    setLoading(true);
    try {
      const params = { folder, max_results: 30 };
      // Build query
      let extraQ = '';
      if (folder === 'INBOX' && category && !searchQ) {
        extraQ = `category:${category}`;
      }
      if (searchQ) {
        extraQ = searchQ;
      }
      if (extraQ) params.q = extraQ;
      if (pageToken) params.page_token = pageToken;

      const r = await axios.get(`${API}/gmail/messages`, { params, withCredentials: true });
      if (pageToken) {
        setMessages(prev => [...prev, ...(r.data.messages || [])]);
      } else {
        setMessages(r.data.messages || []);
      }
      setNextPageToken(r.data.nextPageToken || null);
    } catch (e) {
      if (e.response?.status === 403) {
        setConnected(false);
        toast.error('Gmail session expired. Please reconnect.');
      } else {
        toast.error('Failed to load emails');
      }
    }
    setLoading(false);
  }, [folder, category, searchQ]);

  useEffect(() => {
    if (connected) fetchMessages();
  }, [connected, folder, category, fetchMessages]);

  const handleSearch = (val) => {
    setSearchQ(val);
    // Don't auto-fetch on every keystroke — only when user presses Enter or selects a suggestion
    // But DO fetch contact suggestions as they type
    const plainText = val.replace(/\b(is:|has:|from:|to:|subject:|after:|before:|newer_than:|older_than:|label:|filename:|larger:|smaller:|category:|in:)\S*/g, '').trim();
    if (plainText.length >= 2) {
      axios.get(`${API}/gmail/contact-suggestions`, { params: { q: plainText }, withCredentials: true })
        .then(r => { setSearchSuggestions(r.data || []); setShowSearchSuggestions(true); })
        .catch(() => {});
    } else if (val.length >= 2) {
      // Has operators but no plain text — still show the search-all option
      setSearchSuggestions([]);
      setShowSearchSuggestions(true);
    } else {
      setSearchSuggestions([]);
      setShowSearchSuggestions(false);
    }
  };

  const executeSearch = (query) => {
    const q = query || searchQ;
    setSearchQ(q);
    setShowSearchSuggestions(false);
    setShowOperators(false);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    // Fetch immediately
    setTimeout(() => fetchMessages(), 50);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      executeSearch();
    }
    if (e.key === 'Escape') {
      setShowSearchSuggestions(false);
      setShowOperators(false);
    }
  };

  const selectSearchContact = (contact) => {
    const q = `from:${contact.email}`;
    setSearchQ(q);
    setShowSearchSuggestions(false);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    setTimeout(() => fetchMessages(), 50);
  };

  const handleConnect = async () => {
    setConnectLoading(true);
    try {
      const r = await axios.get(`${API}/oauth/gmail/connect`, { withCredentials: true });
      window.location.href = r.data.auth_url;
    } catch {
      toast.error('Failed to start Gmail connection');
      setConnectLoading(false);
    }
  };

  const handleDisconnect = async () => {
    await axios.post(`${API}/oauth/gmail/disconnect`, {}, { withCredentials: true });
    setConnected(false);
    setMessages([]);
    toast.success('Gmail disconnected');
  };

  const openMessage = async (msg) => {
    try {
      // Mark read in local state immediately for instant UI feedback
      if (msg.is_unread) {
        setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_unread: false } : m));
      }
      const [msgRes, threadRes] = await Promise.all([
        axios.get(`${API}/gmail/messages/${msg.id}`, { withCredentials: true }),
        axios.get(`${API}/gmail/threads/${msg.threadId}`, { withCredentials: true }),
      ]);
      setSelectedMsg(msgRes.data);
      setThreadMessages(threadRes.data.messages);
      // Sync read status to Gmail backend
      if (msg.is_unread) {
        axios.post(`${API}/gmail/messages/${msg.id}/read`, {}, { withCredentials: true })
          .then(() => fetchUnreadCount())
          .catch(() => {});
      }
    } catch {
      toast.error('Failed to load email');
    }
  };

  const handleReply = (msg) => { setReplyTo(msg); setReplyAll(false); setForwardMsg(null); setReplyThreadId(msg.threadId); setShowCompose(true); };
  const handleReplyAll = (msg) => { setReplyTo(msg); setReplyAll(true); setForwardMsg(null); setReplyThreadId(msg.threadId); setShowCompose(true); };
  const handleForward = (msg) => { setForwardMsg(msg); setReplyTo(null); setReplyAll(false); setReplyThreadId(null); setShowCompose(true); };
  const sendTimerRef = useRef(null);

  const handleSend = async (data) => {
    // Delayed send with undo — 5 second window like Gmail
    return new Promise((resolve, reject) => {
      let cancelled = false;
      const toastId = toast('Message sent', {
        duration: 11000,
        action: {
          label: 'Undo',
          onClick: () => {
            cancelled = true;
            if (sendTimerRef.current) clearTimeout(sendTimerRef.current);
            toast.success('Sending cancelled');
            resolve();
          }
        }
      });
      sendTimerRef.current = setTimeout(async () => {
        if (cancelled) return;
        try {
          await axios.post(`${API}/gmail/send`, data, { withCredentials: true });
          fetchMessages();
          resolve();
        } catch (e) {
          toast.dismiss(toastId);
          reject(e);
        }
      }, 10000);
    });
  };

  const handleStar = async (msg) => {
    const ep = msg.is_starred ? 'unstar' : 'star';
    try {
      await axios.post(`${API}/gmail/messages/${msg.id}/${ep}`, {}, { withCredentials: true });
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_starred: !m.is_starred } : m));
      if (selectedMsg?.id === msg.id) setSelectedMsg(prev => ({ ...prev, is_starred: !prev.is_starred }));
    } catch { toast.error('Failed to update star'); }
  };

  const handleArchive = async (msg) => {
    try {
      await axios.post(`${API}/gmail/messages/${msg.id}/archive`, {}, { withCredentials: true });
      setMessages(prev => prev.filter(m => m.id !== msg.id));
      setSelectedMsg(null); setThreadMessages(null);
      fetchUnreadCount();
      toast('Conversation archived', {
        duration: 10000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await axios.post(`${API}/gmail/messages/${msg.id}/unarchive`, {}, { withCredentials: true });
              fetchMessages();
              fetchUnreadCount();
              toast.success('Moved back to Inbox');
            } catch { toast.error('Undo failed'); }
          }
        }
      });
    } catch { toast.error('Failed to archive'); }
  };

  const handleTrash = async (msg) => {
    try {
      await axios.post(`${API}/gmail/messages/${msg.id}/trash`, {}, { withCredentials: true });
      setMessages(prev => prev.filter(m => m.id !== msg.id));
      setSelectedMsg(null); setThreadMessages(null);
      fetchUnreadCount();
      toast('Conversation moved to Trash', {
        duration: 10000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              // Undo trash = untrash
              await axios.post(`${API}/gmail/messages/${msg.id}/untrash`, {}, { withCredentials: true });
              fetchMessages();
              fetchUnreadCount();
              toast.success('Moved back from Trash');
            } catch { toast.error('Undo failed'); }
          }
        }
      });
    } catch { toast.error('Failed to delete'); }
  };

  const handleMarkUnread = async (msg) => {
    try {
      // Update local state immediately
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_unread: true } : m));
      setSelectedMsg(null); setThreadMessages(null);
      await axios.post(`${API}/gmail/messages/${msg.id}/unread`, {}, { withCredentials: true });
      fetchUnreadCount();
      toast('Marked as unread', {
        duration: 10000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await axios.post(`${API}/gmail/messages/${msg.id}/read`, {}, { withCredentials: true });
              setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_unread: false } : m));
              fetchUnreadCount();
              toast.success('Marked as read');
            } catch { toast.error('Undo failed'); }
          }
        }
      });
    } catch {
      // Revert on failure
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_unread: false } : m));
      toast.error('Failed to mark unread');
    }
  };

  const toggleReadInList = async (e, msg) => {
    e.stopPropagation();
    const markingUnread = !msg.is_unread;
    const ep = markingUnread ? 'unread' : 'read';
    // Update local state immediately
    setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_unread: markingUnread } : m));
    try {
      await axios.post(`${API}/gmail/messages/${msg.id}/${ep}`, {}, { withCredentials: true });
      fetchUnreadCount();
    } catch {
      // Revert on failure
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_unread: !markingUnread } : m));
      toast.error('Failed to update');
    }
  };

  const starInList = async (e, msg) => { e.stopPropagation(); await handleStar(msg); };
  const archiveInList = async (e, msg) => { e.stopPropagation(); await handleArchive(msg); };
  const trashInList = async (e, msg) => { e.stopPropagation(); await handleTrash(msg); };

  if (connected === null) {
    return <div className="flex items-center justify-center h-full"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  const folders = [
    { key: 'INBOX', label: 'Inbox', icon: Inbox, count: unreadData.count },
    { key: 'STARRED', label: 'Starred', icon: Star },
    { key: 'SENT', label: 'Sent', icon: Send },
    { key: 'DRAFT', label: 'Drafts', icon: FileText },
    { key: 'TRASH', label: 'Trash', icon: Trash2 },
    { key: 'ALL', label: 'All Mail', icon: Mail },
  ];

  const SEARCH_CHIPS = [
    { label: 'Unread', query: 'in:inbox is:unread' },
  ];

  const SEARCH_OPERATORS = [
    { op: 'is:unread', desc: 'Unread emails' },
    { op: 'is:starred', desc: 'Starred emails' },
    { op: 'is:important', desc: 'Important emails' },
    { op: 'has:attachment', desc: 'With attachments' },
    { op: 'from:', desc: 'From a sender (e.g. from:john@)' },
    { op: 'to:', desc: 'Sent to (e.g. to:amit@)' },
    { op: 'subject:', desc: 'Subject contains (e.g. subject:meeting)' },
    { op: 'after:', desc: 'After date (e.g. after:2026/01/01)' },
    { op: 'before:', desc: 'Before date (e.g. before:2026/03/01)' },
    { op: 'newer_than:', desc: 'Newer than (e.g. newer_than:7d)' },
    { op: 'older_than:', desc: 'Older than (e.g. older_than:30d)' },
    { op: 'label:', desc: 'By label (e.g. label:work)' },
    { op: 'filename:', desc: 'Attachment name (e.g. filename:pdf)' },
    { op: 'larger:', desc: 'Larger than (e.g. larger:5M)' },
    { op: 'smaller:', desc: 'Smaller than (e.g. smaller:1M)' },
  ];

  const toggleChip = (query) => {
    setActiveChips(prev => {
      const next = new Set(prev);
      if (next.has(query)) next.delete(query); else next.add(query);
      // Strip all chip queries from current search text to get manual-only text
      let manualQ = searchQ;
      for (const chip of SEARCH_CHIPS) {
        manualQ = manualQ.replace(chip.query, '');
      }
      manualQ = manualQ.replace(/\s+/g, ' ').trim();
      // Rebuild: manual text + active chips
      const chipQ = [...next].join(' ');
      const combined = [manualQ, chipQ].filter(Boolean).join(' ');
      setSearchQ(combined);
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
      searchTimeout.current = setTimeout(() => fetchMessages(), 200);
      return next;
    });
  };

  const clearSearch = () => {
    setSearchQ('');
    setActiveChips(new Set());
    setSearchSuggestions([]);
    setShowSearchSuggestions(false);
    fetchMessages();
  };

  const insertOperator = (op) => {
    setSearchQ(prev => prev ? `${prev} ${op}` : op);
    setShowOperators(false);
    searchRef.current?.focus();
  };

  return (
    <div className="h-[calc(100vh-2rem)] flex flex-col p-4 gap-2" data-testid="gmail-page">
      {/* Search bar (Gmail-style, full width) */}
      {connected && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                ref={searchRef}
                value={searchQ}
                onChange={e => handleSearch(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                onFocus={() => { if (!searchQ) setShowOperators(true); else if (searchQ.length >= 2) setShowSearchSuggestions(true); }}
                onBlur={() => setTimeout(() => { setShowOperators(false); setShowSearchSuggestions(false); }, 250)}
                placeholder="Search emails..."
                className="pl-10 h-10 rounded-full bg-slate-100 border-0 focus-visible:bg-white focus-visible:ring-1 focus-visible:ring-slate-300 text-sm"
                data-testid="email-search"
              />
              {searchQ && (
                <button onClick={clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              )}
              {/* Gmail-style search dropdown: contacts + search-all option */}
              {showSearchSuggestions && searchQ.length >= 2 && (
                <div className="absolute top-12 left-0 right-0 bg-white rounded-xl shadow-lg border z-50 max-h-80 overflow-y-auto" data-testid="search-suggestions-dropdown">
                  {/* CRM contact matches */}
                  {searchSuggestions.length > 0 && (
                    <>
                      <div className="px-3 py-1.5 border-b bg-slate-50/50">
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">People</p>
                      </div>
                      {searchSuggestions.map((s, i) => (
                        <button key={i} onMouseDown={() => selectSearchContact(s)}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-sky-50 transition-colors">
                          <div className="w-8 h-8 rounded-full bg-sky-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                            {(s.name || s.email)[0].toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <span className="text-sm font-medium text-slate-800 block truncate">{s.name || s.email}</span>
                            <span className="text-xs text-slate-400 truncate block">{s.email}{s.company ? ` · ${s.company}` : ''}</span>
                          </div>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded shrink-0 ${s.type === 'pharma' ? 'bg-teal-50 text-teal-600' : 'bg-amber-50 text-amber-600'}`}>
                            {s.type}
                          </span>
                        </button>
                      ))}
                    </>
                  )}
                  {/* Search all mail option — always shown */}
                  <div className="border-t">
                    <button onMouseDown={() => executeSearch()} className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-sky-50 transition-colors">
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                        <Search className="w-4 h-4 text-slate-500" />
                      </div>
                      <span className="text-sm text-sky-700 font-medium">Search all mail for "<span className="text-slate-800">{searchQ}</span>"</span>
                    </button>
                  </div>
                </div>
              )}
              {/* Operators dropdown — only when empty + focused */}
              {showOperators && !searchQ && !showSearchSuggestions && (
                <div className="absolute top-12 left-0 right-0 bg-white rounded-xl shadow-lg border z-50 max-h-72 overflow-y-auto" data-testid="search-operators">
                  <div className="px-3 py-2 border-b">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Search Operators</p>
                  </div>
                  {SEARCH_OPERATORS.map(o => (
                    <button key={o.op} onMouseDown={() => insertOperator(o.op)}
                      className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 transition-colors">
                      <code className="text-xs text-sky-700 bg-sky-50 px-1.5 py-0.5 rounded font-mono">{o.op}</code>
                      <span className="text-xs text-slate-500">{o.desc}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button size="sm" variant="ghost" onClick={() => fetchMessages()} className="h-10 w-10 p-0 rounded-full" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button size="sm" variant="ghost" onClick={handleDisconnect} className="h-10 w-10 p-0 rounded-full text-slate-400 hover:text-red-500" data-testid="disconnect-btn">
              <Link2Off className="w-4 h-4" />
            </Button>
          </div>
          {/* Quick filter chips */}
          <div className="flex items-center gap-1.5 pl-1">
            {SEARCH_CHIPS.map(chip => (
              <button key={chip.query} onClick={() => toggleChip(chip.query)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                  activeChips.has(chip.query)
                    ? 'bg-[#d3e3fd] border-[#1a73e8]/30 text-[#1a73e8]'
                    : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                }`}
                data-testid={`chip-${chip.label.toLowerCase().replace(/\s/g, '-')}`}>
                {chip.label}
                {activeChips.has(chip.query) && <X className="w-3 h-3 ml-1 inline" />}
              </button>
            ))}
          </div>
        </div>
      )}

      {!connected ? (
        <ConnectPrompt onConnect={handleConnect} loading={connectLoading} />
      ) : (
        <div className="flex-1 flex gap-3 overflow-hidden">
          {/* Sidebar folders */}
          <div className="w-48 shrink-0 space-y-1" data-testid="folder-sidebar">
            <Button onClick={() => { setShowCompose(true); setReplyTo(null); setForwardMsg(null); setReplyThreadId(null); }}
              className="w-full justify-start gap-2 bg-[#c2e7ff] text-[#001d35] hover:bg-[#a8d8ea] rounded-2xl h-12 shadow-sm mb-3 font-medium"
              data-testid="compose-btn">
              <Mail className="w-5 h-5" /> Compose
            </Button>

            {folders.map(f => (
              <button
                key={f.key}
                onClick={() => { setFolder(f.key); setSelectedMsg(null); setThreadMessages(null); if (f.key !== 'INBOX') setCategory(''); else setCategory('primary'); }}
                className={`w-full flex items-center justify-between px-4 py-2 rounded-full text-[13px] font-medium transition-colors ${
                  folder === f.key ? 'bg-[#d3e3fd] text-[#001d35]' : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid={`folder-${f.key.toLowerCase()}`}
              >
                <div className="flex items-center gap-3">
                  <f.icon className="w-4 h-4" />
                  {f.label}
                </div>
                {f.count > 0 && (
                  <span className="text-xs font-semibold" data-testid="unread-badge">{f.count}</span>
                )}
              </button>
            ))}
          </div>

          {/* Main content */}
          <Card className="flex-1 flex flex-col overflow-hidden border-0 shadow-sm">
            {selectedMsg ? (
              <MessageDetail
                message={selectedMsg} threadMessages={threadMessages}
                onBack={() => { setSelectedMsg(null); setThreadMessages(null); }}
                onReply={handleReply} onReplyAll={handleReplyAll} onForward={handleForward}
                onStar={handleStar} onArchive={handleArchive}
                onTrash={handleTrash} onMarkUnread={handleMarkUnread}
              />
            ) : (
              <>
                {/* Category tabs (only in INBOX, hidden during search) */}
                {folder === 'INBOX' && !searchQ && (
                  <div className="flex border-b bg-white shrink-0">
                    {CATEGORIES.map(cat => {
                      const catCount = unreadData.categories?.[cat.key] || 0;
                      return (
                        <button
                          key={cat.key}
                          onClick={() => setCategory(cat.key)}
                          className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                            category === cat.key
                              ? 'border-[#1a73e8] text-[#1a73e8]'
                              : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                          }`}
                          data-testid={`tab-${cat.key}`}
                        >
                          <cat.icon className="w-3.5 h-3.5" />
                          {cat.label}
                          {catCount > 0 && (
                            <span className={`text-[10px] font-bold ml-1 ${category === cat.key ? 'text-[#1a73e8]' : 'text-slate-400'}`}>
                              {catCount}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Search results header */}
                {searchQ && (
                  <div className="flex items-center justify-between px-4 py-2 border-b bg-slate-50 shrink-0">
                    <p className="text-xs text-slate-500">
                      Search results for <span className="font-medium text-slate-700">{searchQ}</span>
                      {!loading && <span className="ml-1">({messages.length} found)</span>}
                    </p>
                    <button onClick={clearSearch} className="text-xs text-sky-600 hover:underline" data-testid="clear-search">
                      Clear search
                    </button>
                  </div>
                )}

                {/* Email list */}
                <div className="flex-1 overflow-y-auto">
                  {loading && messages.length === 0 ? (
                    <div className="flex items-center justify-center py-20">
                      <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-sm text-slate-400">
                      <Inbox className="w-12 h-12 mb-3 text-slate-200" />
                      <p className="font-medium text-slate-500">Nothing here</p>
                      <p className="text-xs mt-1">No emails in this view</p>
                    </div>
                  ) : (
                    <>
                      {messages.map(msg => {
                        const sender = parseEmail(msg.from);
                        return (
                          <div
                            key={msg.id}
                            onClick={() => openMessage(msg)}
                            className={`flex items-center gap-1 pl-2 pr-4 py-1.5 border-b border-slate-100 cursor-pointer hover:shadow-sm transition-all group relative ${
                              msg.is_unread ? 'bg-white border-l-[3px] border-l-[#1a73e8]' : 'bg-[#f8f8f8] border-l-[3px] border-l-transparent'
                            }`}
                            data-testid={`email-row-${msg.id}`}
                          >
                            {/* Star */}
                            <button onClick={e => starInList(e, msg)}
                              className={`p-1 shrink-0 ${msg.is_starred ? 'text-amber-400' : 'text-slate-300 group-hover:text-slate-400 hover:!text-amber-400'}`}
                              data-testid={`star-${msg.id}`}>
                              <Star className="w-4 h-4" fill={msg.is_starred ? 'currentColor' : 'none'} />
                            </button>

                            {/* Sender */}
                            <div className={`w-[180px] shrink-0 truncate text-[13px] pl-1 ${msg.is_unread ? 'font-semibold text-slate-900' : 'text-slate-600'}`}>
                              {sender.name || sender.email}
                            </div>

                            {/* Subject + snippet */}
                            <div className="flex-1 min-w-0 flex items-baseline gap-1.5">
                              <span className={`text-[13px] truncate ${msg.is_unread ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                                {msg.subject || '(no subject)'}
                              </span>
                              <span className="text-[13px] text-slate-400 truncate flex-1">
                                — {decodeHtml(msg.snippet)}
                              </span>
                            </div>

                            {/* Hover actions */}
                            <div className="hidden group-hover:flex items-center gap-0.5 shrink-0 bg-white shadow-sm rounded border px-1 py-0.5 absolute right-14">
                              <button onClick={e => archiveInList(e, msg)} className="p-1 rounded hover:bg-slate-100 text-slate-400" title="Archive">
                                <Archive className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={e => trashInList(e, msg)} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-red-500" title="Delete">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={e => toggleReadInList(e, msg)} className="p-1 rounded hover:bg-slate-100 text-slate-400" title={msg.is_unread ? 'Mark read' : 'Mark unread'}>
                                {msg.is_unread ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                              </button>
                            </div>

                            {/* Date + indicators */}
                            <div className="flex items-center gap-1.5 shrink-0 ml-2">
                              {msg.has_attachments && <Paperclip className="w-3.5 h-3.5 text-slate-400" />}
                              <span className={`text-xs w-16 text-right ${msg.is_unread ? 'font-semibold text-slate-900' : 'text-slate-400'}`}>
                                {fmtDate(msg.date, msg.internalDate)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                      {nextPageToken && (
                        <div className="flex justify-center py-3">
                          <Button size="sm" variant="ghost" onClick={() => fetchMessages(nextPageToken)} disabled={loading} data-testid="load-more-btn">
                            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />}
                            Load more
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </Card>
        </div>
      )}

      {showCompose && (
        <ComposeModal
          onClose={() => { setShowCompose(false); setReplyTo(null); setReplyAll(false); setForwardMsg(null); }}
          onSend={handleSend} replyTo={replyTo} replyAll={replyAll} forwardMsg={forwardMsg} threadId={replyThreadId}
          userEmail="amit.dang@foresightrx.com"
        />
      )}
    </div>
  );
}
